import { forwardRef, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Typography } from '@packages/shared/src/components/Typography/Typography';
import { Button } from '@packages/shared/src/components/Button/Button';
import { Box } from '@packages/shared/src/components/Box/Box';
import { generateTransitionStyles } from '@packages/shared/src/utils/generateTransitionStyles/generateTransitionStyles';

export type ResponsiveFlyoutHeaderProps = {
  onClose?: () => void;
  onBack?: () => void;
  title?: string;
  isVisible?: boolean;
};

const messages = defineMessages({
  back: {
    id: 'header.navigation.flyout.buttons.back',
    defaultMessage: 'Zurück',
  },
  close: {
    id: 'header.navigation.flyout.buttons.close',
    defaultMessage: 'Schließen',
  },
});

/**
 * Header for the navigation popup, displaying the currently selected category, and some controls to navigate back and close the popup.
 */
export const ResponsiveFlyoutHeader = forwardRef<HTMLButtonElement, ResponsiveFlyoutHeaderProps>(
  ({ title, onClose, onBack, isVisible }, ref) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      isVisible && closeButtonRef.current?.focus();
    }, [isVisible]);

    useEffect(() => {
      const onMainNavHandler = (e: KeyboardEvent) => {
        if (e.key === 'Backspace') onBack?.();
        if (e.key === 'Escape') onClose?.();
      };
      document.addEventListener('keydown', onMainNavHandler);

      return () => {
        document.removeEventListener('keydown', onMainNavHandler);
      };
    }, [onBack, onClose]);

    return (
      <Box
        sx={{
          display: { xs: 'none', lg: 'grid' },
          padding: 1,
          alignItems: 'center',
          gridTemplate: '"full" 1fr / auto',
        }}
      >
        <CSSTransition
          appear
          in={!!onBack}
          timeout={250}
          classNames="header-nav-back-button"
          unmountOnExit
          nodeRef={ref}
        >
          <Button
            ref={ref}
            variant="text"
            startIcon={<span className="icon-emp-arrow-medium-left size-4" />}
            onClick={onBack}
            sx={{
              gridArea: 'full',
              justifySelf: 'start',
              typography: 'h5',
              ...generateTransitionStyles({
                classNames: '&.header-nav-back-button',
                initial: {
                  // transition-delay is used to sync the animation with the popup contents, which transition using out-in mode
                  transition: 'transform 125ms ease-in-out 125ms, opacity 125ms ease-in-out 125ms',
                  transform: 'translateX(1rem)',
                  opacity: 0,
                },
                animate: {
                  transform: 'translateX(0)',
                  opacity: 1,
                },
                exit: {
                  transition: 'transform 125ms ease-in-out, opacity 125ms ease-in-out',
                  transform: 'translateX(1rem)',
                  opacity: 0,
                },
              }),
            }}
          >
            <FormattedMessage key="back-text" {...messages.back} />
          </Button>
        </CSSTransition>
        <Typography sx={{ gridArea: 'full', justifySelf: 'center' }} variant="h4">
          {title}
        </Typography>
        <Button
          className="end-button"
          ref={closeButtonRef}
          variant="text"
          endIcon={<span className="icon-emp-cancel size-4" />}
          onClick={() => {
            onClose?.();
          }}
          sx={{ gridArea: 'full', justifySelf: 'end', typography: 'h5' }}
        >
          <FormattedMessage key="close-text" {...messages.close} />
        </Button>
      </Box>
    );
  },
);
